import { useEffect, useRef, useState } from 'react';
// material-ui
import { useTheme } from '@mui/material/styles';
import {
	Badge,
	Button,
	Box,
	Card,
	CardHeader,
	ClickAwayListener,
	FormControl,
	IconButton,
	InputAdornment,
	OutlinedInput,
	Paper,
	Popper,
	Stack,
	Typography,
	useMediaQuery,
} from '@mui/material';

// assets
import { useDispatch, useSelector } from 'react-redux';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import MessageIcon from 'assets/icons/message';
import { CrossIcon } from 'assets/icons/common';

import { useGetMessageCountMutation } from 'store/features/profile/profileApi';

import { setMarkAsRead } from 'store/features/profile/profileSlice';
import MessageContent from './MessageContent';

// sx styles
const cardHeaderSX = {
	backgroundColor: '#FFFFFF',
	color: '#0F172A',
	padding: '16px 20px',
	borderBottom: '1px solid #E2E8F0',
	display: 'flex',
	alignItems: 'center',
	'& .MuiCardHeader-content': {
		my: 0,
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiCardHeader-action': {
		m: 0,
		alignSelf: 'center',
		display: 'flex',
		alignItems: 'center',
	},
};

// ==============================|| HEADER CONTENT - Message ||============================== //

const Message = () => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
	const [searchString, setSearchString] = useState('');

	const [GetMessageCount] = useGetMessageCountMutation();

	const { unreadMessageCount } = useSelector((state) => state?.profile);

	// console.log(profileData, "data");
	const navigate = useNavigate();

	const anchorRef = useRef(null);
	const [open, setOpen] = useState(false);

	const clickHandler = (message) => {
		if (message?.stageId) {
			handleClose();
			navigate('/process-form', { state: { stageId: message?.stageId, action: 'conversation', processCode: message?.processCode } });
		}
	};

	const openMessage = () => {
		if (!open) {
			dispatch(setMarkAsRead({ type: 'TOTAL' }));
			setOpen(true);
		}
	};

	const handleClose = () => {
		// if (!open === false) {
		// }

		setSearchString('');
		setOpen(false);
	};

	const onChatHandler = () => {
		handleClose();
		navigate('/conversation');
	};

	useEffect(() => {
		GetMessageCount();
	}, [GetMessageCount]);

	return (
		<Box sx={{ alignSelf: 'center', flexShrink: 0 }}>
			{!matchesXs && (
				<IconButton
					disableRipple
					sx={{
						color: 'brandDark.contrastText',
						'&:hover': { color: 'secondary.lighter' },
						width: 'auto',
						height: 'auto',
						padding: '0 12px',
						display: 'flex',
						flexDirection: 'row',
						gap: '8px',
						alignItems: 'center',
					}}
					aria-label="open profile"
					ref={anchorRef}
					aria-controls={open ? 'profile-grow' : undefined}
					aria-haspopup="true"
					onClick={openMessage}
				>
					<Badge
						color="error"
						anchorOrigin={{
							vertical: 'top',
							horizontal: 'right',
						}}
						badgeContent={unreadMessageCount}
						sx={{ '.MuiBadge-colorError': { bgcolor: '#B00020', color: '#ffffff', outline: '1px solid #ffff' } }}
						max={99}
					>
						<Stack direction="column" alignItems="center">
							{/* <img src={message} width="20px" alt="message" /> */}
							<MessageIcon width="20px" height="20px" />
						</Stack>
					</Badge>
					<Typography variant="body1">Conversation</Typography>
				</IconButton>
			)}

			{matchesXs && (
				<IconButton
					disableRipple
					sx={{ color: 'brandDark.contrastText', ml: '5px', '&:hover': { color: 'secondary.lighter' } }}
					aria-label="open profile"
					ref={anchorRef}
					aria-controls={open ? 'profile-grow' : undefined}
					aria-haspopup="true"
					onClick={openMessage}
				>
					<Badge
						color="error"
						anchorOrigin={{
							vertical: 'top',
							horizontal: 'right',
						}}
						badgeContent={unreadMessageCount}
						sx={{ '.MuiBadge-colorError': { bgcolor: '#B00020', color: '#ffffff', outline: '1px solid #ffff' } }}
						max={99}
					>
						{/* <img src={message} width="20px" height="auto" alt="message" loading="lazy" /> */}
						<MessageIcon width="20px" height="20px" />
					</Badge>
				</IconButton>
			)}
			<Popper
				placement={matchesXs ? 'bottom' : 'bottom-end'}
				open={open}
				border="none"
				anchorEl={anchorRef.current}
				role={undefined}
				disablePortal
				popperOptions={{
					modifiers: [
						{
							name: 'offset',
							options: {
								offset: [matchesXs ? -5 : 0, 9],
							},
						},
					],
				}}
			>
				<Paper
					sx={{
						boxShadow: '0 18px 45px rgba(15, 23, 42, 0.24)',
						minWidth: 500,
						maxWidth: 520,
						width: 520,
						borderRadius: '12px',
						border: '1px solid #E2E8F0',
						overflow: 'hidden',
						[theme.breakpoints.down('sm')]: {
							minWidth: 'unset',
							maxWidth: 'calc(100vw - 24px)',
							width: 'calc(100vw - 24px)',
							margin: '0 12px',
						},
					}}
				>
					<ClickAwayListener onClickAway={handleClose}>
						<Card
							elevation={0}
							sx={{
								position: 'relative',
								borderRadius: '12px',
							}}
						>
							<CardHeader
								titleTypographyProps={{
									sx: {
										fontSize: '20px',
										fontWeight: 600,
										color: '#101828',
										lineHeight: '28px',
									},
								}}
								action={
									<IconButton
										size="small"
										sx={{
											width: 24,
											height: 24,
											p: 0,
											color: '#99A1AF',
											'&:hover': { color: '#99A1AF', backgroundColor: 'transparent' },
										}}
										onClick={handleClose}
									>
										<CrossIcon width="24px" height="24px" color="#99A1AF" />
									</IconButton>
								}
								title="Conversation"
								sx={cardHeaderSX}
							/>
							<Box sx={{ p: '16px 20px 10px' }}>
								<FormControl sx={{ width: '100%' }}>
									<OutlinedInput
										size="small"
										id="message-search"
										sx={{
											backgroundColor: '#F8FAFC',
											borderRadius: '12px',
											minHeight: '42px',
											'& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
										}}
										startAdornment={
											<InputAdornment position="start" sx={{ mr: -0.5 }}>
												<SearchOutlined style={{ color: '#94A3B8' }} />
											</InputAdornment>
										}
										aria-describedby="message-search-text"
										inputProps={{
											'aria-label': 'weight',
										}}
										onChange={(e) => setSearchString(e.target.value)}
										placeholder="Search"
									/>
								</FormControl>
								<Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
									<Button
										variant="outlined"
										size="small"
										onClick={onChatHandler}
										startIcon={<MessageIcon width="16px" height="16px" />}
										sx={{
											textTransform: 'none',
											borderRadius: '10px',
											borderColor: '#CBD5E1',
											color: '#1D4ED8',
											fontWeight: 500,
											'&:hover': { borderColor: '#93C5FD', backgroundColor: '#EFF6FF' },
										}}
									>
										One-to-One Conversation
									</Button>
								</Box>
							</Box>
							<Box
								sx={{
									width: '100%',
									maxWidth: '100%',
									height: { xs: 'calc(100dvh - 240px)', md: 430 },
									maxHeight: { xs: 'calc(100dvh - 240px)', md: 430 },
									bgcolor: '#FFFFFF',
									position: 'relative',
									borderTop: '1px solid #E2E8F0',
								}}
							>
								<MessageContent searchString={searchString} clickHandler={clickHandler} />
							</Box>
							<Box sx={{ textAlign: 'center', height: '10px', bgcolor: '#fff' }}></Box>
						</Card>
					</ClickAwayListener>
				</Paper>
			</Popper>
		</Box>
	);
};

export default Message;
